import { Panel, RoutedPanelProps } from '@/components/Layout/Panel/Panel';
import { useStackNavigate } from '@/app/navigation/stack-links';
import { usePinnedNav } from '@/features/pinned-nav/pinned-nav.hooks';
import AuthService from '@/services/AuthService';
import { useEffect } from 'react';

/**
 * Root redirect panel that sends authenticated users to their home page — the
 * first pinned sidebar item, or /projects when nothing is pinned — and
 * unauthenticated users to /login.
 *
 * The pins come from the session profile, so they are known synchronously and
 * the redirect never has to wait on a request.
 */
export function RootRedirectPanel({
  path,
  basePath,
  ...panelProps
}: RoutedPanelProps) {
  const go = useStackNavigate();
  const { homeUrl } = usePinnedNav();

  useEffect(() => {
    const authService = AuthService.getInstance();
    const isAuthenticated = authService.isAuthenticated();
    const needsReAuthentication = authService.needsReAuthentication();
    if (isAuthenticated && !needsReAuthentication) {
      go({ to: homeUrl, replace: true });
    }
    // If not authenticated, the useAuthGuard in AppWrapper will handle
    // redirecting to /login
  }, [go, homeUrl]);

  return (
    <Panel {...panelProps}>
      <div className="text-muted-foreground flex h-full items-center justify-center">
        redirecting...
      </div>
    </Panel>
  );
}
